import React, { useState, useEffect } from 'react';

// BackButton component (no changes)
const BackButton = () => {
    const goBack = () => {
        window.history.back();
    };

    return (
        <button
            onClick={goBack}
            style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#FFF',
                fontSize: '1.5em',
                padding: '0'
            }}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
        </button>
    );
};

const ChatBot = () => {
    const businessOptions = [
        "E-commerce Store",
        "Digital Marketing Agency",
        "SaaS (Software as a Service)",
        "Content Creation / Blogging",
        "Online Coaching / Consulting"
    ];

    const functionalityOptions = [
        "User",
        "Vendor",
        "Admin",
        "Employee"
    ];

    const [messages, setMessages] = useState([{
        sender: 'ai',
        text: 'What type of business do you want to start?'
    }]);
    const [input, setInput] = useState('');
    const [isInitialQuestionAnswered, setIsInitialQuestionAnswered] = useState(false);
    const [isSecondQuestionAnswered, setIsSecondQuestionAnswered] = useState(false);
    const [selectedBusiness, setSelectedBusiness] = useState('');
    const [selectedFunctionalities, setSelectedFunctionalities] = useState([]);

    const [displayedHeading, setDisplayedHeading] = useState('');
    const headingText = "I'm here to help you create and improve your online business budget.";

    useEffect(() => {
        let i = 0;
        const typingInterval = setInterval(() => {
            if (i < headingText.length) {
                setDisplayedHeading(prev => prev + headingText.charAt(i));
                i++;
            } else {
                clearInterval(typingInterval);
            }
        }, 25);

        return () => clearInterval(typingInterval);
    }, []);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (input.trim() === '') return;

        const userMessage = { sender: 'user', text: input };
        setMessages(prevMessages => [...prevMessages, userMessage]);

        const aiResponse = getAIResponse(input);
        setTimeout(() => {
            const aiMessage = { sender: 'ai', text: aiResponse };
            setMessages(prevMessages => [...prevMessages, aiMessage]);
        }, 1000);

        setInput('');
    };

    const handleBusinessSelect = (option) => {
        setSelectedBusiness(option);
    };

    const handleFunctionalitySelect = (option) => {
        setSelectedFunctionalities(prev => {
            if (prev.includes(option)) {
                return prev.filter(item => item !== option);
            } else {
                return [...prev, option];
            }
        });
    };

    const handleFirstQuestionSubmit = () => {
        if (!selectedBusiness) return;

        const userMessage = { sender: 'user', text: selectedBusiness };
        setMessages(prevMessages => [...prevMessages, userMessage]);

        const aiResponse = `Great choice! Now, what type of functionality do you want? (You can choose more than one.)`;
        setTimeout(() => {
            const aiMessage = { sender: 'ai', text: aiResponse };
            setMessages(prevMessages => [...prevMessages, aiMessage]);
        }, 1000);

        setIsInitialQuestionAnswered(true);
    };

    const handleSecondQuestionSubmit = () => {
        if (selectedFunctionalities.length === 0) return;

        const userMessage = { sender: 'user', text: selectedFunctionalities.join(', ') };
        setMessages(prevMessages => [...prevMessages, userMessage]);

        const aiResponse = `Understood. To help you budget for a business with **${selectedFunctionalities.join(', ')}** functionality, please provide your projected revenue for the first year.`;
        setTimeout(() => {
            const aiMessage = { sender: 'ai', text: aiResponse };
            setMessages(prevMessages => [...prevMessages, aiMessage]);
        }, 1000);

        setIsSecondQuestionAnswered(true);
    };

    const getAIResponse = (query) => {
        const lowerCaseQuery = query.toLowerCase();
        if (lowerCaseQuery.includes('revenue')) {
            return "Got it. Now, what percentage of that do you want to allocate to marketing?";
        }
        return "Thank you. Let's move on to the next question.";
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: 'Roboto, sans-serif',
            position: 'relative',
        }}>

            <style>
                {`
                @keyframes blinking-cursor {
                    from, to { border-color: transparent }
                    50% { border-color: #333 }
                }
                `}
            </style>

            <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                maxWidth: '40%',
                zIndex: 100,
                color: '#333',
                fontFamily: 'monospace',
                fontSize: '1em',
                lineHeight: '1.2em',
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                borderRight: '0.15em solid #333',
                animation: 'blinking-cursor 0.75s step-end infinite',
                paddingRight: '5px'
            }}>
                {displayedHeading}
            </div>

            <div style={{
                width: '90%',
                maxWidth: '500px',
                height: '80vh',
                maxHeight: '700px',
                borderRadius: '20px',
                backgroundColor: '#F0F2F5',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                margin: '20px auto',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 1
            }}>
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '15px',
                    backgroundColor: '#007BFF',
                    color: '#FFF',
                    borderBottom: '1px solid #E0E0E0',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 'clamp(1em, 4vw, 1.5em)'
                }}>
                    <BackButton />
                    AI Assistant
                </div>

                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {messages.map((msg, index) => (
                        <div
                            key={index}
                            style={{
                                alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                backgroundColor: msg.sender === 'user' ? '#DCF8C6' : '#FFF',
                                padding: '10px 15px',
                                borderRadius: '15px',
                                maxWidth: '80%',
                                wordWrap: 'break-word',
                                color: '#333',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                marginBottom: '10px'
                            }}
                        >
                            {msg.text}
                        </div>
                    ))}
                </div>

                {/* Conditional rendering for questions or regular input */}
                {!isInitialQuestionAnswered ? (
                    <div style={{ padding: '0px', display: 'flex', flexDirection: 'column' }}>
                        {businessOptions.map(option => (
                            <label key={option} style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '10px',
                                backgroundColor: '#FFF',
                                border: selectedBusiness === option ? '2px solid #007BFF' : '1px solid #E0E0E0',
                                marginBottom: '0px'
                            }}>
                                <input
                                    type="radio"
                                    name="businessType"
                                    value={option}
                                    checked={selectedBusiness === option}
                                    onChange={() => handleBusinessSelect(option)}
                                    style={{ marginRight: '10px' }}
                                />
                                {option}
                            </label>
                        ))}
                        <button
                            onClick={handleFirstQuestionSubmit}
                            disabled={!selectedBusiness}
                            style={{
                                padding: '10px',
                                borderRadius: '0',
                                border: 'none',
                                backgroundColor: selectedBusiness ? '#007BFF' : '#B0B0B0',
                                color: '#FFF',
                                cursor: selectedBusiness ? 'pointer' : 'not-allowed',
                                marginTop: '10px'
                            }}
                        >
                            Continue
                        </button>
                    </div>
                ) : !isSecondQuestionAnswered ? (
                    <div style={{ padding: '0px', display: 'flex', flexDirection: 'column' }}>
                        {functionalityOptions.map(option => (
                            <label key={option} style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '10px',
                                backgroundColor: '#FFF',
                                border: selectedFunctionalities.includes(option) ? '2px solid #007BFF' : '1px solid #E0E0E0',
                                marginBottom: '0px'
                            }}>
                                <input
                                    type="checkbox"
                                    name="functionalityType"
                                    value={option}
                                    checked={selectedFunctionalities.includes(option)}
                                    onChange={() => handleFunctionalitySelect(option)}
                                    style={{ marginRight: '10px' }}
                                />
                                {option}
                            </label>
                        ))}
                        <button
                            onClick={handleSecondQuestionSubmit}
                            disabled={selectedFunctionalities.length === 0}
                            style={{
                                padding: '10px',
                                borderRadius: '0',
                                border: 'none',
                                backgroundColor: selectedFunctionalities.length > 0 ? '#007BFF' : '#B0B0B0',
                                color: '#FFF',
                                cursor: selectedFunctionalities.length > 0 ? 'pointer' : 'not-allowed',
                                marginTop: '10px'
                            }}
                        >
                            Continue
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSendMessage} style={{
                        display: 'flex',
                        padding: '10px',
                        borderTop: '1px solid #E0E0E0',
                        backgroundColor: '#FFF'
                    }}>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your answer..."
                            style={{
                                flex: 1,
                                padding: '10px',
                                borderRadius: '20px',
                                border: '1px solid #E0E0E0',
                                marginRight: '10px',
                                outline: 'none',
                                fontSize: 'clamp(0.8em, 2vw, 1em)'
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                backgroundColor: '#007BFF',
                                color: '#FFF',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                padding: '0'
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ transform: 'rotate(45deg)', marginTop: '-2px', marginRight: '-2px' }}
                            >
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ChatBot;